import React, { useState, useEffect } from 'react'
import { useStaticQuery, graphql, Link } from 'gatsby'
import { useQuery } from '@apollo/react-hooks'
import axios from 'axios'
import qs from 'qs'
import tw, { css, styled } from 'twin.macro'

/** Page components */
import Layout from '../../../components/layout'
import Meta from '../../../components/meta'
import Map from '../../../components/map'
/** Hooks */
import useCart from '../../../hooks/useCart'
/** Queries */
import { ORDER } from '../../../app/schema/order'
/** Styles with Emotion & Tailwindcss */
const Checkout = styled.div`
	${tw`flex flex-col items-center w-full bg-gray-300`}
`
const Container = tw.div`px-2 md:px-0 py-4 w-full`
const Heading1 = tw.h1`font-display text-2xl mb-2`
const Details = styled.div`
	${tw`flex flex-col md:flex-row w-full my-2 bg-white`}
	${tw`border-2 border-gray-400 rounded`}
	${tw`py-3 px-2`}
`
const Shipping = tw.div`flex flex-col w-full md:w-6/12 md:mr-2`
const Items = styled.div`
	${tw`flex md:flex-col w-full my-2 bg-white`}
	${tw`border-2 border-gray-400 rounded`}
`
const Row = styled.div`
	${tw`flex flex-row justify-between w-full text-gray-600`}
	div {
		${tw`px-2`}
	}
	&.head {
		div {
			${tw`text-gray-500 text-sm uppercase font-bold pt-3 pb-1`}
		}
	}
	&.tax {
		${tw`border-t-2 border-gray-400`}
	}
	&.total {
		div {
			${tw`text-gray-700 bg-gray-300 pb-1 pt-1`}
		}
	}
	&.shipping {
		div {
			&:first-of-type {
				${tw`invisible`}
			}
		}
	}
`
const Col = styled.div`
	${tw`w-2/12 p-2`}
	&:first-of-type {
		${tw`pl-5`}
	}
	&:nth-of-type(2) {
		${tw`w-4/12`}
	}
	&:last-of-type {
		${tw`self-end pr-5`}
	}
`

const isBrowser = typeof window !== 'undefined'

export const CheckoutSuccessPage = props => {
	const { items, clearCart, freight, fee, tax, total, orderTotal } = useCart()
	const { page } = useStaticQuery(graphql`
		query checkoutSuccessPageQuery {
			page: allContentfulPage(filter: { slug: { eq: "order-complete" } }) {
				edges {
					node {
						slug
						seoTitle
						seoKeywords
						seoDescription
					}
				}
			}
		}
	`)
	const { slug, seoTitle, seoKeywords, seoDescription } = page.edges[1].node
	const { order_id } = qs.parse(props?.location?.search)
	const { data } = useQuery(ORDER, {
		variables: { _id: String(order_id) },
	})
	// console.log(JSON.stringify(data, undefined, 2))
	// console.log(JSON.stringify(error, undefined, 2))

	useEffect(() => {
		/**
		 * Google Analytics - Tag Manager - Enhanced Ecommerce
		 * Measuring checkout steps - 2. Checkout page
		 * */
		const dataLayer = !!(isBrowser && window.dataLayer)
		const hasCart = total > 1
		const hasId = !!order_id
		if (dataLayer && hasCart && hasId) {
			// const order = data && data.order
			const products = items.map(
				({ name, price, sku, quantity, brand, category }) => ({
					name,
					id: sku,
					price: Number(price / 100).toFixed(2) * 1,
					quantity,
					brand,
					category,
				})
			)
			const data = {
				event: 'purchase',
				ecommerce: {
					purchase: {
						actionField: {
							id: order_id.toString(), // Transaction ID. Required for purchases and refunds.
							affiliation: 'Online Store',
							revenue: Number(total / 100).toFixed(2) * 1, // Total transaction value (incl. tax and shipping)
							tax: Number(tax / 100).toFixed(2) * 1,
							shipping: Number(freight / 100).toFixed(2) * 1,
						},
						products,
					},
				},
			}
			window.dataLayer.push(data)
			clearCart()
		}
		return () => {}
	}, [items, data, total, order_id, tax, freight, clearCart])

	const [consumer, setConsumer] = useState(null)

	useEffect(() => {
		/** Get & Set consumer coordinates */
		const order = data && data.order
		if (order) {
			const isCustomer = () => {
				if (order.shipping.address.state !== order.billing.address.state) {
					return order.shipping
				}
				return order && order.billing
			}
			const customer = isCustomer()
			const getConsumer = async () => {
				const data = await getGeocode(customer)
				return setConsumer({
					coordinates: data.features[0].geometry.coordinates,
				})
			}
			getConsumer()
		}
		return () => {}
	}, [data])
	/** Map Data {Object} */
	const mapData = data &&
		data.order.dealer &&
		consumer && {
			dealer: { coordinates: data.order.dealer.location.coordinates },
			consumer,
		}
	/** Order Date {Date} */
	const orderDate = data && data.order.date && new Date(data.order.date)
	return (
		<Layout>
			<Meta
				title={seoTitle}
				keywords={seoKeywords}
				description={seoDescription}
			/>
			<Checkout id={slug}>
				<Container className="container">
					<Heading1>{seoTitle}</Heading1>
					{data ? (
						<>
							<p css={tw`my-4 text-lg`}>
								Congratulations <b>{data.order.shipping.firstName}</b>, your
								order is on itʼs way to you in{' '}
								{data.order.shipping.address.city}.
							</p>
							<b css={tw`my-2`}>Shipping</b>
							<Details>
								<Shipping>
									<Row className="">
										<Col css={tw`w-3/12`}>Order ID:</Col>
										<Col css={tw`w-9/12`}>{data.order.orderId}</Col>
									</Row>
									<Row className="">
										<Col css={tw`w-3/12`}>Order Placed:</Col>
										<Col css={tw`w-9/12`}>
											{orderDate.toLocaleString('en-AU', {
												timezone: 'Australia/Brisbane',
											})}
										</Col>
									</Row>
									<Row className="">
										<Col css={tw`w-3/12`}>Shipping From:</Col>
										<Col css={tw`w-9/12`}>
											<a
												href={
													data.order.dealer.social.minisite
														? `https://stores.mydolphin.com.au/${data.order.dealer.social.minisite}`
														: ``
												}
												target="_blank"
												rel="noopener noreferrer"
												css={tw`flex text-blue-500`}
											>
												{data.order.dealer.name}
											</a>
											{data.order.dealer.address.fullAddress}
										</Col>
									</Row>
									<Row className="">
										<Col css={tw`w-3/12`}>Shipping To:</Col>
										<Col css={tw`w-9/12`}>
											<span css={tw`flex text-yellow-500`}>
												{data.order.shipping.name}
											</span>
											{data.order.shipping.address.fullAddress}
										</Col>
									</Row>
								</Shipping>
								{mapData && mapData.consumer && <Map data={mapData} />}
							</Details>
							<b css={tw`my-2`}>Items</b>
							<Items css={tw`md:block hidden`}>
								<Row className="head">
									<Col>PRODUCT</Col>
									<Col>DESCRIPTION</Col>
									<Col>QUANTITY</Col>
									<Col>PRICE</Col>
									<Col>TOTAL</Col>
								</Row>
								{data.order.product.map(({ name, sku, price, quantity }) => (
									<Row key={sku} className="sku">
										<Col>{name}</Col>
										<Col>{sku}</Col>
										<Col>{quantity}</Col>
										<Col>
											{Number(price / 100)
												.toFixed(2)
												.toLocaleString('en-AU', {
													style: 'currency',
													currency: 'AUD',
												})}
										</Col>
										<Col>
											{Number((price * quantity) / 100)
												.toFixed(2)
												.toLocaleString('en-AU', {
													style: 'currency',
													currency: 'AUD',
												})}
										</Col>
									</Row>
								))}
								{data.order.freight.map(({ name, sku, price, quantity }) => (
									<Row key={sku} className="sku">
										<Col>{name}</Col>
										<Col>{sku}</Col>
										<Col>{quantity}</Col>
										<Col>
											{Number(price / 100)
												.toFixed(2)
												.toLocaleString('en-AU', {
													style: 'currency',
													currency: 'AUD',
												})}
										</Col>
										<Col>
											{Number(price / 100)
												.toFixed(2)
												.toLocaleString('en-AU', {
													style: 'currency',
													currency: 'AUD',
												})}
										</Col>
									</Row>
								))}
								<Row className="tax">
									<Col></Col>
									<Col>Subtotal</Col>
									<Col></Col>
									<Col></Col>
									<Col>${Number(data.order.subtotal / 100).toFixed(2)}</Col>
								</Row>
								<Row className="tax">
									<Col></Col>
									<Col>GST</Col>
									<Col></Col>
									<Col></Col>
									<Col>${Number(data.order.tax / 100).toFixed(2)}</Col>
								</Row>
								<Row className="total">
									<Col className="total">Total</Col>
									<Col></Col>
									<Col></Col>
									<Col></Col>
									<Col>${Number(data.order.total / 100).toFixed(2)}</Col>
								</Row>
							</Items>
							<Items css={tw`block md:hidden`}>
								<div css={tw`grid grid-cols-4 w-full font-bold text-sm mb-2 p-2`}>
									<div>PRODUCT</div>
									<div>QUANTITY</div>
									<div>PRICE</div>
									<div>TOTAL</div>
								</div>
								{data.order.product.map(({ name, sku, price, quantity }) => (
									<div css={tw`grid grid-cols-4 w-full text-sm mb-2 p-2`}>
										<div>{name}</div>
										<div>{quantity}</div>
										<div>{Number(price / 100)
												.toFixed(2)
												.toLocaleString('en-AU', {
													style: 'currency',
													currency: 'AUD',
												})}</div>
										<div>{Number((price * quantity) / 100)
												.toFixed(2)
												.toLocaleString('en-AU', {
													style: 'currency',
													currency: 'AUD',
												})}</div>
									</div>
								))}
								{data.order.freight.map(({ name, sku, price, quantity }) => (
									<div css={tw`grid grid-cols-4 w-full text-sm p-2`}>
										<div>{name}</div>
										<div>{quantity}</div>
										<div>{Number(price / 100)
												.toFixed(2)
												.toLocaleString('en-AU', {
													style: 'currency',
													currency: 'AUD',
												})}</div>
										<div>{Number((price * quantity) / 100)
												.toFixed(2)
												.toLocaleString('en-AU', {
													style: 'currency',
													currency: 'AUD',
												})}</div>
									</div>
								))}
								<div css={tw`grid grid-cols-4 w-full text-sm mt-4 border-t-2 border-gray-600 p-2`}>
										<div css={tw`col-start-3`}>Subtotal</div>
										<div>${Number(data.order.subtotal / 100).toFixed(2)}</div>
								</div>
								<div css={tw`grid grid-cols-4 w-full text-sm border-t-2 border-gray-600 p-2`}>
										<div css={tw`col-start-3`}>GST</div>
										<div>${Number(data.order.tax / 100).toFixed(2)}</div>
								</div>
								<div css={tw`grid grid-cols-4 w-full text-sm text-gray-700 bg-gray-300 py-2`}>
										<div css={tw`col-start-3`}>Total</div>
										<div>${Number(data.order.total / 100).toFixed(2)}</div>
								</div>
							</Items>
						</>
					) : (
						<p css={tw`my-4 text-lg`}>
							{order_id ? (
								<>...loading</>
							) : (
								<>
									Sorry somethings not right, please check your link and try
									again
									{!order_id && ', no order ID detected'}.{' '}
									<Link
										to="/shop/checkout/"
										css={tw`text-blue-600 font-bold underline`}
									>
										Go back ←
									</Link>
								</>
							)}
						</p>
					)}
				</Container>
			</Checkout>
		</Layout>
	)
}

export default CheckoutSuccessPage

const MAPBOX_TOKEN = process.env.GATSBY_MAPBOX_TOKEN
export const getGeocode = async args => {
	try {
		const {
			address: { fullAddress, postalCode },
			getCoordinates,
		} = args
		const ENDPOINT = 'mapbox.places'
		const SEARCH_TEXT = encodeURI(
			getCoordinates ? getCoordinates : fullAddress ? fullAddress : postalCode
		)
		const URI = `https://api.mapbox.com/geocoding/v5/${ENDPOINT}/${SEARCH_TEXT}.json?access_token=${MAPBOX_TOKEN}&autocomplete=true&country=au&types=postcode%2Caddress&limit=1`
		const response = await axios.get(URI)
		if (response.status === 404)
			throw new Error('MapBox found no matches for that address / postcode.')
		return response.data
	} catch (error) {
		return ''
	}
}
